import React, { useState, useEffect, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { Model } from '../../../../types/models/model'
import { RunInferenceResponse } from '../../../../types/prediction'
import RealTimeImage from '../../../RealTimeImage'
import PillsBBox from '../../../PillsBBox'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'
import PillsClassification from '../../../PillsClassification'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'

import {
  FaClock,
  FaChartLine,
  FaExpand,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa'

interface ComparisonInferenceViewProps {
  results1: RunInferenceResponse
  results2: RunInferenceResponse
  dropFiles: File[] | null
  model1?: Model
  model2?: Model
  onReset: () => void
  showConfidence: boolean
  onToggleConfidence?: () => void
}

// Bootstrap utility-based wrappers (replacing styled-components)
type DivProps = React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }

const ComparisonContainer = ({ children, className = '', style, ...rest }: DivProps) => (
  <div
    {...rest}
    className={`d-flex flex-column gap-3 ${className}`}
    style={{ ...(style || {}), height: 'auto', maxHeight: 'calc(100vh - 200px)' }}
  >
    {children}
  </div>
)

const MainContent = ({ children, className = '', style, ...rest }: DivProps) => (
  <div
    {...rest}
    className={`${className}`}
    style={{
      ...(style || {}),
      display: 'grid',
      gridTemplateColumns: '350px 1fr',
      gap: '1rem',
      flex: 1,
      minHeight: 0,
    }}
  >
    {children}
  </div>
)

const PanelTitle = ({ children }: { children?: React.ReactNode }) => (
  <h3
    className="text-white fw-bold d-flex align-items-center justify-content-center"
    style={{ fontSize: '1.2rem', margin: 0, gap: '.75rem' }}
  >
    {children}
  </h3>
)

const PerformancePanel = ({ children }: { children?: React.ReactNode }) => (
  <div
    className="rounded-3 p-4 d-flex flex-column"
    style={{
      background: 'rgba(0,0,0,0.2)',
      border: '2px solid rgba(59,130,246,0.3)',
      gap: '1.5rem',
    }}
  >
    {children}
  </div>
)

const MetricsGrid = ({ children }: { children?: React.ReactNode }) => (
  <div className="row row-cols-2 g-3">{children}</div>
)

const MetricCard = ({
  children,
  type,
}: {
  children?: React.ReactNode
  type?: 'speed' | 'accuracy' | 'agreement'
}) => (
  <div className="col">
    <div
      className="rounded p-3 text-center"
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid',
        borderColor:
          type === 'speed'
            ? 'rgba(59,130,246,0.3)'
            : type === 'accuracy'
            ? 'rgba(16,185,129,0.3)'
            : type === 'agreement'
            ? 'rgba(168,85,247,0.3)'
            : 'rgba(255,255,255,0.1)',
      }}
    >
      {children}
    </div>
  </div>
)

const MetricValue = ({
  children,
  highlight,
}: {
  children?: React.ReactNode
  highlight?: boolean
}) => (
  <div
    className="fw-bold"
    style={{ color: highlight ? '#10b981' : '#ffffff', fontSize: '1.5rem', marginBottom: '.25rem' }}
  >
    {children}
  </div>
)

const MetricLabel = ({ children }: { children?: React.ReactNode }) => (
  <div
    className="text-white-75 fw-semibold text-uppercase"
    style={{ fontSize: '.8rem', letterSpacing: '.5px' }}
  >
    {children}
  </div>
)

const DetailedView = ({ children }: { children?: React.ReactNode }) => (
  <div
    className="rounded-3"
    style={{
      background: 'rgba(0,0,0,0.2)',
      border: '2px solid rgba(255,255,255,0.1)',
      overflow: 'hidden',
    }}
  >
    {children}
  </div>
)

const DetailedHeader = ({
  children,
  className = '',
}: {
  children?: React.ReactNode
  className?: string
}) => (
  <div
    className={`d-flex align-items-center justify-content-between p-3 border-bottom ${className}`}
    style={{ background: 'rgba(0,0,0,0.4)' }}
  >
    {children}
  </div>
)

const DetailedTitle = ({ children }: { children?: React.ReactNode }) => (
  <h3 className="mb-0 text-white fw-bold" style={{ fontSize: '1.1rem' }}>
    {children}
  </h3>
)

const HeaderControls = ({ children }: { children?: React.ReactNode }) => (
  <div className="d-flex align-items-center ms-auto" style={{ gap: '1rem' }}>
    {children}
  </div>
)

const NavButton = ({
  children,
  disabled,
  onClick,
}: {
  children?: React.ReactNode
  disabled?: boolean
  onClick?: () => void
}) => (
  <button
    type="button"
    className="btn btn-sm btn-outline-primary"
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
)

const ImageCounter = ({ children }: { children?: React.ReactNode }) => (
  <div className="text-white-50 fw-semibold" style={{ fontSize: '.8rem', padding: '0 .5rem' }}>
    {children}
  </div>
)

const ExpandButton = ({
  onClick,
  children,
}: {
  onClick?: () => void
  children?: React.ReactNode
}) => (
  <button type="button" className="btn btn-sm btn-outline-primary" onClick={onClick}>
    {children ?? <FaExpand />}
  </button>
)

const ComparisonGrid = ({ children }: { children?: React.ReactNode }) => (
  <div className="row" style={{ height: '400px' }}>
    {children}
  </div>
)

const ModelSection = ({ children, isLeft }: { children?: React.ReactNode; isLeft?: boolean }) => (
  <div
    className={`col-12 col-md-6 d-flex flex-column${isLeft ? ' border-end' : ''}`}
    style={{ borderColor: 'rgba(255,255,255,0.1)' }}
  >
    {children}
  </div>
)

const ModelHeader = ({
  children,
  model,
}: {
  children?: React.ReactNode
  model?: 'base' | 'iteration'
}) => (
  <div
    style={{
      background: model === 'base' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(52, 211, 153, 0.2)',
      borderBottom: `2px solid ${model === 'base' ? '#6366f1' : '#34d399'}`,
      padding: '.5rem',
      textAlign: 'center',
    }}
  >
    {children}
  </div>
)

const ModelName = ({ children }: { children?: React.ReactNode }) => (
  <div className="text-white fw-bold" style={{ fontSize: '.9rem', marginBottom: '.25rem' }}>
    {children}
  </div>
)

const ModelResult = ({ children }: { children?: React.ReactNode }) => (
  <div className="text-white-75" style={{ fontSize: '.8rem' }}>
    {children}
  </div>
)

const ImageContainer = ({ children }: { children?: React.ReactNode }) => (
  <div
    className="d-flex align-items-center justify-content-center p-2"
    style={{ background: 'rgba(0,0,0,0.1)', height: '100%', width: '100%' }}
  >
    {children}
  </div>
)

interface ImageData {
  url: string
  width: number
  height: number
}

// remove legacy styled-components block

const ComparisonInferenceView: React.FC<ComparisonInferenceViewProps> = ({
  results1,
  results2,
  dropFiles,
  model1,
  model2,
  showConfidence,
  onReset,
  onToggleConfidence,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [images, setImages] = useState<ImageData[]>([])
  const [showModal, setShowModal] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  useEffect(() => {
    const urls: string[] = []

    const loadImage = (file: File): Promise<ImageData> => {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file)
        urls.push(url)
        const img = new Image()

        img.onload = () => {
          resolve({
            url,
            width: img.naturalWidth,
            height: img.naturalHeight,
          })
        }

        img.src = url
      })
    }

    const loadImages = async () => {
      if (!dropFiles) return
      const loadedImages = await Promise.all(dropFiles.map(loadImage))
      setImages(loadedImages)
    }

    loadImages()

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [dropFiles])

  // Build robust pairing by unioning filenames and aligning results/images
  const paired = useMemo(() => {
    const map1 = new Map<string, (typeof results1)[number]>()
    const map2 = new Map<string, (typeof results2)[number]>()
    results1?.forEach((r) => map1.set(r.filename, r))
    results2?.forEach((r) => map2.set(r.filename, r))

    // Prefer dropFiles order if provided, else union of filenames sorted
    const filenamesFromDrops = dropFiles?.map((f) => f.name) || []
    const unionSet = new Set<string>([...map1.keys(), ...map2.keys(), ...filenamesFromDrops])
    const filenames = filenamesFromDrops.length
      ? filenamesFromDrops.filter((n) => unionSet.has(n))
      : Array.from(unionSet).sort((a, b) => a.localeCompare(b))

    // Map image per filename using same index as dropFiles if present
    const fileToImage = new Map<string, ImageData>()
    if (dropFiles && images.length === dropFiles.length) {
      dropFiles.forEach((file, idx) => {
        const img = images[idx]
        if (img) fileToImage.set(file.name, img)
      })
    }

    return filenames.map((name) => ({
      filename: name,
      result1: map1.get(name),
      result2: map2.get(name),
      image: fileToImage.get(name),
    }))
  }, [results1, results2, dropFiles, images])

  // Calculate batch statistics
  const batchStats = useMemo(() => {
    if (!paired.length) {
      return {
        agree: 0,
        differ: 0,
        lowConfidence: 0,
        avgConfidence1: 0,
        avgConfidence2: 0,
      }
    }

    let agree = 0
    let differ = 0
    let lowConfidence = 0
    let totalConfidence1 = 0
    let totalConfidence2 = 0
    let count1 = 0
    let count2 = 0

    paired.forEach(({ result1, result2 }) => {
      const pred1 = result1?.predictions?.[0]
      const pred2 = result2?.predictions?.[0]

      if (pred1) {
        totalConfidence1 += pred1.confidence || 0
        count1++
      }
      if (pred2) {
        totalConfidence2 += pred2.confidence || 0
        count2++
      }

      const conf1 = pred1?.confidence || 0
      const conf2 = pred2?.confidence || 0

      if (!pred1 || !pred2) return
      if (conf1 < 0.5 || conf2 < 0.5) {
        lowConfidence++
      } else if (pred1?.class_name === pred2?.class_name) {
        agree++
      } else {
        differ++
      }
    })

    return {
      agree,
      differ,
      lowConfidence,
      avgConfidence1: count1 ? totalConfidence1 / count1 : 0,
      avgConfidence2: count2 ? totalConfidence2 / count2 : 0,
    }
  }, [paired])

  const calculateCardSize = (imageData: ImageData) => {
    const maxSize = 300
    const aspectRatio = imageData.width / imageData.height
    return aspectRatio > 1 ? Math.min(maxSize, imageData.width) : Math.min(maxSize, imageData.width)
  }

  if (!paired.length || !model1 || !model2) return null

  const selected = paired[Math.min(selectedImageIndex, Math.max(0, paired.length - 1))]
  const selectedImage = selected?.image
  const selectedResult1 = selected?.result1
  const selectedResult2 = selected?.result2

  return (
    <ComparisonContainer>
      {/* Main Content */}
      <MainContent>
        {/* Performance Metrics Panel */}
        <PerformancePanel>
          <PanelTitle>
            <FaChartLine />
            Performance Metrics
          </PanelTitle>
          <MetricsGrid>
            <MetricCard type="speed">
              <MetricValue>~120ms</MetricValue>
              <MetricLabel>
                <FaClock /> Avg Response Time
              </MetricLabel>
            </MetricCard>
            <MetricCard type="accuracy">
              <MetricValue highlight>
                {Math.round(
                  (batchStats.agree /
                    (batchStats.agree + batchStats.differ + batchStats.lowConfidence)) *
                    100 || 0
                )}
                %
              </MetricValue>
              <MetricLabel>
                <FaChartLine /> Agreement Rate
              </MetricLabel>
            </MetricCard>
            <MetricCard type="agreement">
              <MetricValue>
                {Math.round(Math.abs(batchStats.avgConfidence1 - batchStats.avgConfidence2) * 100) /
                  100}
              </MetricValue>
              <MetricLabel>Confidence Difference</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{paired.length}</MetricValue>
              <MetricLabel>Images Processed</MetricLabel>
            </MetricCard>
          </MetricsGrid>
        </PerformancePanel>

        {/* Detailed Comparison */}
        <DetailedView>
          <DetailedHeader className="position-relative">
            {/* Left-justified truncated title with tooltip */}
            <div
              className="text-truncate"
              title={
                paired[selectedImageIndex]?.filename ||
                dropFiles?.[selectedImageIndex]?.name ||
                'Selected Image'
              }
              style={{ maxWidth: '50%' }}
            >
              <DetailedTitle>
                {paired[selectedImageIndex]?.filename ||
                  dropFiles?.[selectedImageIndex]?.name ||
                  'Selected Image'}
              </DetailedTitle>
            </div>

            {/* Centered navigation controls */}
            {paired.length > 1 && (
              <div
                className="position-absolute start-50 top-50 translate-middle d-flex align-items-center"
                style={{ gap: '.5rem' }}
              >
                <NavButton
                  disabled={selectedImageIndex === 0}
                  onClick={() => setSelectedImageIndex((prev) => Math.max(0, prev - 1))}
                >
                  <FaChevronLeft />
                </NavButton>
                <ImageCounter>
                  {selectedImageIndex + 1} of {paired.length}
                </ImageCounter>
                <NavButton
                  disabled={selectedImageIndex === paired.length - 1}
                  onClick={() =>
                    setSelectedImageIndex((prev) => Math.min(paired.length - 1, prev + 1))
                  }
                >
                  <FaChevronRight />
                </NavButton>
              </div>
            )}

            {/* Right-aligned controls: Reset / Show% then Expand */}
            <HeaderControls>
              <button className="btn btn-sm btn-warning me-2" onClick={onReset}>
                Reset
              </button>
              <button
                className="btn btn-sm btn-outline-primary me-3 d-flex align-items-center gap-2"
                onClick={onToggleConfidence}
                disabled={!onToggleConfidence}
              >
                {showConfidence ? (
                  <>
                    <FaEyeSlash /> Hide %
                  </>
                ) : (
                  <>
                    <FaEye /> Show %
                  </>
                )}
              </button>
              <ExpandButton onClick={() => setShowModal(true)}>
                <FaExpand />
              </ExpandButton>
            </HeaderControls>
          </DetailedHeader>

          <ComparisonGrid>
            <ModelSection isLeft>
              <ModelHeader model="base">
                <ModelName>{model1.name}</ModelName>
                <ModelResult>
                  {selectedResult1?.predictions?.[0]?.class_name || 'No prediction'}
                  {selectedResult1?.predictions?.[0]?.confidence &&
                    ` (${Math.round((selectedResult1.predictions[0].confidence || 0) * 100)}%)`}
                </ModelResult>
              </ModelHeader>
              <div className="d-flex align-items-start" style={{ gap: '1rem' }}>
                <div
                  className="flex-shrink-0 p-2"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  {project?.annotation_type === 'classification' ? (
                    <PillsClassification
                      tagNames={(selectedResult1?.predictions || []).map((p) => p.class_name)}
                      tagConfidence={(selectedResult1?.predictions || []).map((p) => p.confidence)}
                      maxTags={100}
                      className="text-start"
                      large={false}
                    />
                  ) : (
                    <PillsBBox
                      bounding_boxes={
                        (selectedResult1?.predictions || [])
                          .filter((p) => !!p.bounding_boxes)
                          .map((p) => ({
                            name: p.class_name,
                            points: (p.bounding_boxes?.points as number[]) || [],
                            ai_labeled: true,
                            class_index: p.class_index,
                            class_name: p.class_name,
                            confidence: p.confidence,
                          })) as AnnotationDataBoundingBox[]
                      }
                      showTagCounts
                      maxTags={100}
                      direction="column"
                    />
                  )}
                </div>
                <ImageContainer>
                  {selectedResult1 && selectedImage ? (
                    <RealTimeImage
                      result={selectedResult1}
                      imageData={selectedImage}
                      cardSize={calculateCardSize(selectedImage)}
                      showConfidence={showConfidence}
                    />
                  ) : (
                    <div style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                      No image/results available
                    </div>
                  )}
                </ImageContainer>
              </div>
            </ModelSection>

            <ModelSection>
              <ModelHeader model="iteration">
                <ModelName>{model2.name}</ModelName>
                <ModelResult>
                  {selectedResult2?.predictions?.[0]?.class_name || 'No prediction'}
                  {selectedResult2?.predictions?.[0]?.confidence &&
                    ` (${Math.round((selectedResult2.predictions[0].confidence || 0) * 100)}%)`}
                </ModelResult>
              </ModelHeader>
              <div className="d-flex align-items-start" style={{ gap: '1rem' }}>
                <ImageContainer>
                  {selectedResult2 && selectedImage ? (
                    <RealTimeImage
                      result={selectedResult2}
                      imageData={selectedImage}
                      cardSize={calculateCardSize(selectedImage)}
                      showConfidence={showConfidence}
                    />
                  ) : (
                    <div style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                      No image/results available
                    </div>
                  )}
                </ImageContainer>
                <div
                  className="flex-shrink-0 p-2"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  {project?.annotation_type === 'classification' ? (
                    <PillsClassification
                      tagNames={(selectedResult2?.predictions || []).map((p) => p.class_name)}
                      tagConfidence={(selectedResult2?.predictions || []).map((p) => p.confidence)}
                      maxTags={100}
                      className="ms-auto text-end"
                      large={false}
                    />
                  ) : (
                    <PillsBBox
                      bounding_boxes={
                        (selectedResult2?.predictions || [])
                          .filter((p) => !!p.bounding_boxes)
                          .map((p) => ({
                            name: p.class_name,
                            points: (p.bounding_boxes?.points as number[]) || [],
                            ai_labeled: true,
                            class_index: p.class_index,
                            class_name: p.class_name,
                            confidence: p.confidence,
                          })) as AnnotationDataBoundingBox[]
                      }
                      showTagCounts
                      maxTags={100}
                      direction="column"
                    />
                  )}
                </div>
              </div>
            </ModelSection>
          </ComparisonGrid>
        </DetailedView>
      </MainContent>

      {/* Full Size Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="modal-xxl"
        contentClassName="bg-dark border-secondary"
        style={{ ['--bs-modal-width' as unknown as string]: '95vw' }}
      >
        <Modal.Header closeButton closeVariant="white" className="bg-dark border-secondary">
          <Modal.Title className="w-100 text-white">
            <div
              className="d-grid align-items-center w-100"
              style={{ gridTemplateColumns: '1fr auto 1fr', columnGap: '1rem' }}
            >
              <div
                className="text-truncate me-3"
                title={
                  paired[selectedImageIndex]?.filename ||
                  dropFiles?.[selectedImageIndex]?.name ||
                  'Full Size Comparison'
                }
                style={{ minWidth: 0 }}
              >
                {paired[selectedImageIndex]?.filename ||
                  dropFiles?.[selectedImageIndex]?.name ||
                  'Full Size Comparison'}
              </div>
              <div
                className="d-flex align-items-center justify-content-center text-nowrap"
                style={{ gap: '1rem' }}
              >
                <NavButton
                  disabled={selectedImageIndex === 0}
                  onClick={() => setSelectedImageIndex((prev) => Math.max(0, prev - 1))}
                >
                  <FaChevronLeft />
                </NavButton>
                <ImageCounter>
                  {selectedImageIndex + 1} of {paired.length || 1}
                </ImageCounter>
                <NavButton
                  disabled={selectedImageIndex === Math.max(0, (paired.length || 1) - 1)}
                  onClick={() =>
                    setSelectedImageIndex((prev) =>
                      Math.min(Math.max(0, (paired.length || 1) - 1), prev + 1)
                    )
                  }
                >
                  <FaChevronRight />
                </NavButton>
              </div>
              <div className="d-flex justify-content-end">
                <button
                  className="mx-3 btn btn-sm btn-outline-primary text-nowrap"
                  onClick={onToggleConfidence}
                  disabled={!onToggleConfidence}
                >
                  {showConfidence ? 'Hide %' : 'Show %'}
                </button>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2rem',
              width: '100%',
              height: '100%',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <h5 style={{ color: '#6366f1', marginBottom: '1rem' }}>{model1.name}</h5>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                }}
              >
                {selectedResult1 && selectedImage ? (
                  <RealTimeImage
                    result={selectedResult1}
                    imageData={selectedImage}
                    cardSize={Math.min(600, selectedImage.width)}
                    showConfidence={showConfidence}
                  />
                ) : (
                  <div style={{ color: '#b0b0b0' }}>No image/results available</div>
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <h5 style={{ color: '#34d399', marginBottom: '1rem' }}>{model2.name}</h5>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                }}
              >
                {selectedResult2 && selectedImage ? (
                  <RealTimeImage
                    result={selectedResult2}
                    imageData={selectedImage}
                    cardSize={Math.min(600, selectedImage.width)}
                    showConfidence={showConfidence}
                  />
                ) : (
                  <div style={{ color: '#b0b0b0' }}>No image/results available</div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </ComparisonContainer>
  )
}

export default ComparisonInferenceView
